// My Mutt Life 3.0 — shared storefront behaviour

const debounce = (fn, wait = 250) => {
  let timer;
  return (...args) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), wait);
  };
};

const getFocusable = (container) => Array.from(container.querySelectorAll(
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
)).filter((element) => !element.hasAttribute('hidden'));

async function getCart() {
  const response = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Basket could not be loaded.');
  return response.json();
}

function updateCartCounts(cart) {
  document.querySelectorAll('.header__cart-count').forEach((count) => {
    count.textContent = cart.item_count;
    count.classList.toggle('is-empty', cart.item_count === 0);
  });
  document.querySelectorAll('[data-cart-open]').forEach((button) => {
    button.setAttribute('aria-label', `Open basket, ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`);
  });
}

async function refreshCartDrawer(openAfter = false) {
  const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
  if (!response.ok) throw new Error('Basket could not be refreshed.');
  const html = await response.text();
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const nextDrawer = parsed.querySelector('[data-cart-drawer]');
  const currentDrawer = document.querySelector('[data-cart-drawer]');
  if (nextDrawer && currentDrawer) currentDrawer.replaceWith(nextDrawer);
  const cart = await getCart();
  updateCartCounts(cart);
  if (openAfter) requestAnimationFrame(() => document.querySelector('[data-cart-drawer]')?.open());
  return cart;
}

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-cart-panel]');
    this.previouslyFocused = null;
    this.querySelectorAll('[data-cart-close]').forEach((button) => button.addEventListener('click', () => this.close()));
    this.addEventListener('keydown', (event) => this.onKeydown(event));
  }

  open() {
    this.previouslyFocused = document.activeElement;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    this.panel?.removeAttribute('inert');
    document.body.classList.add('is-locked');
    document.querySelectorAll('[data-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    requestAnimationFrame(() => this.querySelector('[data-cart-close]')?.focus());
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    this.panel?.setAttribute('inert', '');
    document.body.classList.remove('is-locked');
    document.querySelectorAll('[data-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    if (this.previouslyFocused instanceof HTMLElement) this.previouslyFocused.focus();
  }

  onKeydown(event) {
    if (!this.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab' || !this.panel) return;
    const focusable = getFocusable(this.panel);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[type="submit"]');
    this.error = this.querySelector('[data-product-error]');
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.form || !this.button || this.button.disabled) return;
    this.button.disabled = true;
    this.button.classList.add('loading');
    if (this.error) this.error.hidden = true;
    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(this.form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'This item could not be added to your basket.');
      if (document.querySelector('[data-cart-drawer]')) {
        await refreshCartDrawer(true);
      } else {
        updateCartCounts(await getCart());
        window.location.href = window.routes.cart_url;
      }
    } catch (error) {
      if (this.error) {
        this.error.textContent = error.message;
        this.error.hidden = false;
        this.error.classList.remove('visually-hidden');
      }
    } finally {
      this.button.disabled = false;
      this.button.classList.remove('loading');
    }
  }
}

if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelector('[data-action="decrease"]')?.addEventListener('click', () => this.change(-1));
    this.querySelector('[data-action="increase"]')?.addEventListener('click', () => this.change(1));
    this.input?.addEventListener('change', () => this.validate());
  }

  change(amount) {
    if (!this.input) return;
    const min = Number.parseInt(this.input.min, 10) || 0;
    const max = Number.parseInt(this.input.max, 10) || 9999;
    this.input.value = Math.min(max, Math.max(min, (Number.parseInt(this.input.value, 10) || min) + amount));
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  validate() {
    if (!this.input) return;
    const min = Number.parseInt(this.input.min, 10) || 0;
    const max = Number.parseInt(this.input.max, 10) || 9999;
    this.input.value = Math.min(max, Math.max(min, Number.parseInt(this.input.value, 10) || min));
  }
}

if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || this.innerHTML.trim()) return;
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html').querySelector('product-recommendations');
        if (html?.innerHTML.trim()) this.innerHTML = html.innerHTML;
      })
      .catch(() => {});
  }
}

if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

function initMobileNavigation(scope = document) {
  const toggle = scope.querySelector('[data-mobile-menu-toggle]');
  const navigation = scope.querySelector('[data-mobile-navigation]');
  if (!toggle || !navigation || toggle.dataset.ready) return;
  toggle.dataset.ready = 'true';
  const setOpen = (open) => {
    toggle.setAttribute('aria-expanded', String(open));
    toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    navigation.hidden = !open;
  };
  toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'));
  navigation.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setOpen(false)));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
      setOpen(false);
      toggle.focus();
    }
  });
  window.addEventListener('resize', debounce(() => {
    if (window.innerWidth > 780) setOpen(false);
  }));
}

function initPredictiveSearch(scope = document) {
  const form = scope.querySelector('[data-predictive-search-form]');
  const input = scope.querySelector('[data-predictive-search-input]');
  const results = scope.querySelector('[data-predictive-search-results]');
  if (!form || !input || !results || input.dataset.ready) return;
  input.dataset.ready = 'true';
  const close = () => {
    results.hidden = true;
    input.setAttribute('aria-expanded', 'false');
  };
  const search = debounce(async () => {
    const term = input.value.trim();
    if (term.length < 2) {
      close();
      return;
    }
    results.innerHTML = '<p class="mml-predictive__heading">Sniffing out the useful stuff…</p>';
    results.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    try {
      const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(term)}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
      const response = await fetch(url);
      if (!response.ok) throw new Error();
      results.innerHTML = await response.text();
    } catch {
      results.innerHTML = '<p class="mml-predictive__heading">Search is having a quick lie down. Press Enter to see results.</p>';
    }
  }, 220);
  input.addEventListener('input', search);
  input.addEventListener('focus', () => { if (input.value.trim().length >= 2) search(); });
  document.addEventListener('click', (event) => { if (!form.parentElement.contains(event.target)) close(); });
  form.addEventListener('keydown', (event) => { if (event.key === 'Escape') { close(); input.focus(); } });
}

function initProduct(scope = document) {
  scope.querySelectorAll('[data-product-root]').forEach((root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const mainImage = root.querySelector('[data-product-main-image]');
    root.querySelectorAll('[data-product-thumb]').forEach((button) => button.addEventListener('click', () => {
      if (mainImage) {
        mainImage.src = button.dataset.imageSrc;
        mainImage.alt = button.dataset.imageAlt || '';
      }
      root.querySelectorAll('[data-product-thumb]').forEach((thumb) => thumb.classList.toggle('is-active', thumb === button));
    }));

    const select = root.querySelector('[data-variant-select]');
    if (!select) return;
    select.addEventListener('change', () => {
      const option = select.options[select.selectedIndex];
      const available = option.dataset.available === 'true';
      const compare = root.querySelector('[data-product-compare]');
      const availability = root.querySelector('[data-product-availability]');
      const addButton = root.querySelector('[data-add-button]');
      const addText = root.querySelector('[data-add-button-text]');
      root.querySelector('[data-product-price]').textContent = option.dataset.price;
      if (compare) {
        compare.textContent = option.dataset.compare || '';
        compare.classList.toggle('is-hidden', !option.dataset.compare);
      }
      if (availability) {
        availability.lastChild.textContent = available ? 'In stock and ready to order' : 'Currently unavailable';
        availability.classList.toggle('is-unavailable', !available);
      }
      if (addButton) addButton.disabled = !available;
      if (addText) addText.textContent = available ? 'Add to basket' : 'Sold out';
      if (option.dataset.image && mainImage) {
        mainImage.src = option.dataset.image;
        mainImage.alt = option.dataset.imageAlt || '';
      }
      if (option.dataset.url) window.history.replaceState({}, '', option.dataset.url);
    });
  });
}

function initBedFinders(scope = document) {
  scope.querySelectorAll('[data-bed-finder]').forEach((finder) => {
    if (finder.dataset.ready) return;
    finder.dataset.ready = 'true';
    const result = finder.querySelector('[data-finder-result]');
    finder.querySelectorAll('[data-finder-option]').forEach((button) => button.addEventListener('click', () => {
      finder.querySelectorAll('[data-finder-option]').forEach((item) => item.classList.toggle('is-selected', item === button));
      if (!result) return;
      result.replaceChildren();
      const heading = document.createElement('strong');
      heading.textContent = button.dataset.resultHeading;
      const copy = document.createElement('span');
      copy.textContent = button.dataset.resultCopy;
      result.append(heading, copy);
      if (button.dataset.resultUrl) {
        const link = document.createElement('a');
        link.href = button.dataset.resultUrl;
        link.textContent = button.dataset.resultLink || 'See suitable beds →';
        result.append(link);
      }
    }));
  });
}

function initCollectionControls(scope = document) {
  scope.querySelectorAll('[data-sort-select]').forEach((select) => {
    if (select.dataset.ready) return;
    select.dataset.ready = 'true';
    select.addEventListener('change', () => select.form?.submit());
  });
}

function initTheme(scope = document) {
  initMobileNavigation(scope);
  initPredictiveSearch(scope);
  initProduct(scope);
  initBedFinders(scope);
  initCollectionControls(scope);
}

document.addEventListener('click', async (event) => {
  const openButton = event.target.closest('[data-cart-open]');
  if (openButton) {
    event.preventDefault();
    document.querySelector('[data-cart-drawer]')?.open();
    return;
  }
  const changeButton = event.target.closest('[data-cart-change]');
  if (!changeButton) return;
  changeButton.disabled = true;
  try {
    const response = await fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(changeButton.dataset.line), quantity: Number(changeButton.dataset.quantity) })
    });
    if (!response.ok) throw new Error();
    await refreshCartDrawer(true);
  } catch {
    changeButton.disabled = false;
  }
});

document.addEventListener('input', debounce(async (event) => {
  if (!event.target.matches('[data-cart-note]')) return;
  await fetch(window.routes.cart_update_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ note: event.target.value })
  });
}, 500));

document.addEventListener('DOMContentLoaded', () => initTheme(document));
document.addEventListener('shopify:section:load', (event) => initTheme(event.target));
